(() => {
  'use strict';

  const GIFT_WRAP_ATTRIBUTE = 'Complimentary gift wrap';
  const GIFT_MESSAGE_ATTRIBUTE = 'Gift message';
  const PRODUCT_COPY = 'Check this box for our fabulous complimentary gift wrap';
  const MESSAGE_LABEL = 'Gift message (optional)';
  const MESSAGE_HELP = 'Write your message exactly as you would like it to appear on the card.';
  const CART_COPY = 'Add our fabulous complimentary gift wrap';
  let cartRequest = null;
  let saveTimer = null;

  const jsonRoute = (route, fallback) => {
    const value = route || fallback;
    return value.endsWith('.js') ? value : `${value}.js`;
  };

  const routes = {
    update: jsonRoute(window.theme?.routes?.cart_update_url, '/cart/update'),
    cart: jsonRoute(window.theme?.routes?.cart_url, '/cart')
  };

  const requestJson = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(options.headers || {})
      },
      ...options
    });

    if (!response.ok) {
      throw new Error(`Gift options request failed (${response.status})`);
    }

    return response.json();
  };

  const getCart = (fresh = false) => {
    if (fresh || !cartRequest) {
      cartRequest = requestJson(routes.cart).catch((error) => {
        cartRequest = null;
        throw error;
      });
    }
    return cartRequest;
  };

  const cartHasGiftWrap = (cart) => (
    cart.attributes?.[GIFT_WRAP_ATTRIBUTE] === 'Yes'
  );

  const setStatus = (panel, message, isError = false) => {
    const status = panel.querySelector('.ss-gift-wrap-status');
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('is-error', isError);
  };

  const setBusy = (panel, busy) => {
    panel.classList.toggle('is-busy', busy);
    panel.querySelectorAll('input, textarea').forEach((field) => {
      field.disabled = busy;
    });
  };

  const syncGiftWrapControls = (checked) => {
    document.querySelectorAll('.ss-gift-wrap-toggle').forEach((checkbox) => {
      checkbox.checked = checked;
    });
  };

  const syncGiftMessageControls = (value) => {
    document.querySelectorAll('.ss-gift-message-field textarea').forEach((textarea) => {
      if (textarea.value !== value) textarea.value = value;
    });
  };

  const savedStatus = (cart) => {
    const hasWrap = cartHasGiftWrap(cart);
    const hasMessage = Boolean((cart.note || '').trim());

    if (hasWrap && hasMessage) return 'Gift wrap and message saved';
    if (hasWrap) return 'Complimentary gift wrap selected';
    if (hasMessage) return 'Gift message saved';
    return '';
  };

  const saveGiftMessage = async (panel, value) => {
    const message = value.trim();
    setStatus(panel, message ? 'Saving message…' : 'Removing message…');

    try {
      await requestJson(routes.update, {
        method: 'POST',
        body: JSON.stringify({
          note: value,
          attributes: {
            [GIFT_MESSAGE_ATTRIBUTE]: message
          }
        })
      });
      cartRequest = null;
      syncGiftMessageControls(value);
      setStatus(panel, message ? 'Gift message saved' : 'Gift message removed');
    } catch (error) {
      console.error(error);
      setStatus(panel, 'Please try saving your message again.', true);
    }
  };

  const updateGiftWrap = async (panel, checked) => {
    setBusy(panel, true);
    setStatus(panel, checked ? 'Saving gift wrap…' : 'Removing gift wrap…');

    try {
      await requestJson(routes.update, {
        method: 'POST',
        body: JSON.stringify({
          attributes: {
            [GIFT_WRAP_ATTRIBUTE]: checked ? 'Yes' : ''
          }
        })
      });
      cartRequest = null;
      syncGiftWrapControls(checked);
      setStatus(panel, checked ? 'Complimentary gift wrap selected' : 'Gift wrap removed');
    } catch (error) {
      console.error(error);
      syncGiftWrapControls(!checked);
      setStatus(panel, 'We could not save gift wrap. Please try again.', true);
    } finally {
      setBusy(panel, false);
    }
  };

  const createCartPanel = (scope) => {
    const panel = document.createElement('section');
    const suffix = scope === 'drawer' ? 'drawer' : 'page';
    panel.className = 'ss-gift-wrap-cart';
    panel.dataset.giftWrapEnhanced = 'true';
    panel.innerHTML = `
      <div class="ss-gift-wrap-choice">
        <input class="ss-gift-wrap-toggle" type="checkbox" id="ss-gift-wrap-${suffix}">
        <label for="ss-gift-wrap-${suffix}">${CART_COPY}</label>
      </div>
      <div class="ss-gift-message-field">
        <label for="ss-gift-message-${suffix}">${MESSAGE_LABEL}</label>
        <textarea id="ss-gift-message-${suffix}" rows="3" placeholder="${MESSAGE_HELP}"></textarea>
        <p class="ss-gift-message-help">${MESSAGE_HELP}</p>
      </div>
      <p class="ss-gift-wrap-status" role="status" aria-live="polite"></p>
    `;

    const checkbox = panel.querySelector('.ss-gift-wrap-toggle');
    const textarea = panel.querySelector('textarea');

    checkbox.addEventListener('change', () => updateGiftWrap(panel, checkbox.checked));
    textarea.addEventListener('input', () => {
      window.clearTimeout(saveTimer);
      setStatus(panel, 'Message not yet saved');
      saveTimer = window.setTimeout(() => saveGiftMessage(panel, textarea.value), 700);
    });
    textarea.addEventListener('blur', () => {
      window.clearTimeout(saveTimer);
      saveGiftMessage(panel, textarea.value);
    });

    getCart().then((cart) => {
      checkbox.checked = cartHasGiftWrap(cart);
      textarea.value = cart.note || '';
      setStatus(panel, savedStatus(cart));
    }).catch((error) => {
      console.error(error);
      setStatus(panel, 'Gift options are temporarily unavailable.', true);
    });

    return panel;
  };

  const enhanceCart = () => {
    const drawerFooter = document.querySelector('#Cart-Drawer .cart-drawer-footer');
    if (drawerFooter && !drawerFooter.querySelector('.ss-gift-wrap-cart')) {
      const buttons = drawerFooter.querySelector('.cart-drawer-buttons');
      drawerFooter.insertBefore(createCartPanel('drawer'), buttons || null);
      drawerFooter.querySelector('cart-notes')?.classList.add('ss-legacy-cart-notes');
    }

    const cartPanel = document.querySelector('#Cart .thb-cart-collaterals');
    if (cartPanel && !cartPanel.querySelector('.ss-gift-wrap-cart')) {
      const totals = cartPanel.querySelector('table.collaterals');
      cartPanel.insertBefore(createCartPanel('page'), totals || cartPanel.firstChild);
      cartPanel.querySelector('.field.cart-notes')?.classList.add('ss-legacy-cart-notes');
    }
  };

  const enhanceProductGiftWrap = () => {
    document.querySelectorAll('.show_frontend').forEach((container, index) => {
      if (container.dataset.giftWrapEnhanced === 'true') return;

      const checkbox = container.querySelector('input[name="giftwrap_frontend"], #giftwrap_input');
      const choice = container.querySelector('.checkboxFive');
      const copy = container.querySelector('.zt_gift_label');
      const note = container.querySelector('.zt_message_note');
      const textarea = note?.querySelector('textarea');

      if (!checkbox || !choice || !copy || !note || !textarea) return;

      container.dataset.giftWrapEnhanced = 'true';
      container.classList.add('ss-gift-wrap-product');
      copy.textContent = PRODUCT_COPY;

      const noteId = textarea.id || `ss-product-gift-message-${index}`;
      textarea.id = noteId;
      textarea.placeholder = MESSAGE_HELP;
      checkbox.setAttribute('aria-controls', noteId);

      if (!note.querySelector('.ss-gift-message-label')) {
        const label = document.createElement('label');
        label.className = 'ss-gift-message-label';
        label.htmlFor = noteId;
        label.textContent = MESSAGE_LABEL;
        note.insertBefore(label, textarea);

        const help = document.createElement('p');
        help.className = 'ss-gift-message-help';
        help.textContent = MESSAGE_HELP;
        textarea.insertAdjacentElement('afterend', help);
      }

      const syncNote = () => {
        const open = checkbox.checked;
        checkbox.setAttribute('aria-expanded', String(open));
        note.style.display = open ? 'block' : 'none';
        if (open) window.setTimeout(() => textarea.focus({ preventScroll: true }), 0);
      };

      checkbox.addEventListener('change', () => window.setTimeout(syncNote, 0));
      choice.addEventListener('click', (event) => {
        if (event.target.closest('input, label, button, a')) return;
        checkbox.click();
      });
      syncNote();
    });
  };

  const enhanceAll = () => {
    enhanceProductGiftWrap();
    enhanceCart();
  };

  const observer = new MutationObserver(() => {
    window.clearTimeout(observer.timer);
    observer.timer = window.setTimeout(enhanceAll, 80);
  });

  const boot = () => {
    enhanceAll();
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('cart:item-added', () => {
    cartRequest = null;
    window.setTimeout(enhanceAll, 350);
  });
  document.addEventListener('line-item:change:end', () => {
    cartRequest = null;
    window.setTimeout(enhanceAll, 350);
  });
})();
